import React, { useState, useEffect } from "react"
import request from "request"
import Modal from "Modal"
import Button from "Button"
import Input from "Input"
import Checkbox from "Checkbox"
import Switch from "Switch"
import { copyText } from "./webhookHelpers"

// Create or edit a webhook endpoint. On create the secret is shown exactly
// once, like an API key.
const WebhookEndpointModal = ({ isOpen, onClose, onSaved, endpoint, availableEvents }) => {
  const isEdit = !!endpoint?.id
  const [name, setName] = useState("")
  const [url, setUrl] = useState("")
  const [events, setEvents] = useState([])
  const [active, setActive] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [created, setCreated] = useState(null)

  useEffect(() => {
    if (!isOpen) return
    setName(endpoint?.name || "")
    setUrl(endpoint?.url || "")
    setEvents(endpoint?.events || availableEvents.map((e) => e.type))
    setActive(endpoint ? endpoint.active : true)
    setError(null)
    setCreated(null)
  }, [isOpen, endpoint, availableEvents])

  const toggleEvent = (type, checked) => {
    setEvents((current) => (checked ? [...new Set([...current, type])] : current.filter((t) => t !== type)))
  }

  const handleSave = () => {
    if (!name.trim()) return setError("Please give this endpoint a name")
    if (!url.trim()) return setError("Please enter the URL Catch should POST to")
    if (events.length === 0) return setError("Select at least one event")

    setIsLoading(true)
    setError(null)
    const payload = { webhook_endpoint: { name: name.trim(), url: url.trim(), events, active } }
    const req = isEdit
      ? request.patch(`/venue_admin/webhook_endpoints/${endpoint.id}`, payload)
      : request.post("/venue_admin/webhook_endpoints", payload)

    req
      .then(({ data }) => {
        setIsLoading(false)
        if (isEdit) {
          onSaved(data.webhookEndpoint)
          onClose()
        } else {
          setCreated(data.webhookEndpoint)
        }
      })
      .catch((err) => {
        setError(err.response?.data?.error || "Failed to save webhook endpoint")
        setIsLoading(false)
      })
  }

  const handleDone = () => {
    onSaved(created)
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="box">
        <h2 className="title is-4">
          {created ? "Webhook Endpoint Created" : isEdit ? "Edit Webhook Endpoint" : "New Webhook Endpoint"}
        </h2>

        {!created ? (
          <>
            <Input
              label="Name"
              value={name}
              onChange={setName}
              placeholder="e.g., Flicket production"
              disabled={isLoading}
            />

            <Input
              label="URL"
              value={url}
              onChange={setUrl}
              placeholder="https://example.com/webhooks/catch"
              disabled={isLoading}
            />
            <p className="help mb-4">
              Catch sends a signed JSON <code>POST</code> to this URL for every subscribed event. It must be reachable from the internet over HTTPS.
            </p>

            <label className="label">Events</label>
            {availableEvents.map((event) => (
              <div key={event.type} className="mb-2">
                <Checkbox
                  label={<span><code>{event.type}</code> — {event.description}</span>}
                  value={events.includes(event.type)}
                  onChange={(checked) => toggleEvent(event.type, checked)}
                />
              </div>
            ))}

            {isEdit && (
              <Switch label="Active" value={active} onChange={setActive} />
            )}

            {error && <div className="notification is-danger is-light">{error}</div>}

            <div className="buttons is-right mt-4">
              <Button text="Cancel" onClick={onClose} disabled={isLoading} />
              <Button
                text={isEdit ? "Save" : "Create Endpoint"}
                color="primary"
                onClick={handleSave}
                loading={isLoading}
              />
            </div>
          </>
        ) : (
          <>
            <div className="notification is-warning">
              <p className="has-text-weight-bold">
                Copy the signing secret now. It is shown only this once; you can rotate it later if it is lost.
              </p>
            </div>

            <div className="field">
              <label className="label">Signing secret</label>
              <div className="field has-addons">
                <div className="control is-expanded">
                  <input className="input is-family-monospace" type="text" value={created.secret} readOnly />
                </div>
                <div className="control">
                  <Button
                    text="Copy"
                    color="primary"
                    onClick={() => copyText(created.secret, "Signing secret copied to clipboard")}
                    icon="copy"
                  />
                </div>
              </div>
              <p className="help">
                Verify each request by recomputing HMAC-SHA256 over <code>{"<timestamp>.<raw body>"}</code> with this
                secret and comparing it to <code>v1</code> in the <code>X-Catch-Signature</code> header. Details are in the
                API docs at <a href="/api-docs" target="_blank" rel="noreferrer">/api-docs</a>.
              </p>
            </div>

            <div className="buttons is-right mt-4">
              <Button text="Done" color="primary" onClick={handleDone} />
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

export default WebhookEndpointModal
